import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, Zap, TrendingUp, Users, Clock } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';

// ── Agent display map ────────────────────────────────────────────

const AGENT_MAP: Record<string, { name: string; emoji: string; color: string }> = {
  'account-manager': { name: 'Lykke', emoji: '🤝', color: '#2e024c' },
  'maya-seo':        { name: 'Maya',  emoji: '🔍', color: '#00b1a5' },
  'leo-perf':        { name: 'Leo',   emoji: '📊', color: '#ea580c' },
  'aria-content':    { name: 'Aria',  emoji: '✍️', color: '#6366f1' },
  'rex-influencer':  { name: 'Rex',   emoji: '👥', color: '#0ea5e9' },
  'nova-strategy':   { name: 'Nova',  emoji: '🧠', color: '#be43ff' },
  'kai-analytics':   { name: 'Kai',   emoji: '📈', color: '#059669' },
  'jones-artdir':    { name: 'Jones', emoji: '🎨', color: '#f59e0b' },
};

function agentInfo(id: string) {
  return AGENT_MAP[id] ?? { name: id, emoji: '🤖', color: '#6b7280' };
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatCost(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

// ── Component ────────────────────────────────────────────────────

interface UsageData {
  agents: Array<{ agentId: string; totalTokensIn: number; totalTokensOut: number; totalCostUsd: number }>;
  daily: Array<{ date: string; tokensIn: number; tokensOut: number; costUsd: number }>;
  plan: { plan: string; creditsRemaining: number; creditsMonthly: number; trialExpired: boolean };
}

export function UsagePage({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [planInfo, setPlanInfo] = useState<{ plan: string; creditsRemaining: number; creditsMonthly: number; trialExpired: boolean } | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      coworkApi.usage.overview(days),
      coworkApi.stripe.getPlan().catch(() => null),
    ]).then(([usage, plan]) => {
      setData(usage);
      if (plan) {
        setPlanInfo({
          plan: plan.plan,
          creditsRemaining: plan.creditsRemaining,
          creditsMonthly: plan.creditsTotal || 0,
          trialExpired: false,
        });
      } else {
        setPlanInfo(usage.plan);
      }
    }).finally(() => setLoading(false));
  }, [days]);

  const totalCost = data?.agents.reduce((s, a) => s + a.totalCostUsd, 0) ?? 0;
  const totalTokensIn = data?.agents.reduce((s, a) => s + a.totalTokensIn, 0) ?? 0;
  const totalTokensOut = data?.agents.reduce((s, a) => s + a.totalTokensOut, 0) ?? 0;
  const creditsUsedPct = planInfo && planInfo.creditsMonthly > 0
    ? Math.min(100, Math.round(((planInfo.creditsMonthly - planInfo.creditsRemaining) / planInfo.creditsMonthly) * 100))
    : 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-6 py-3.5 bg-white border-b border-gray-100">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[12px] text-[#98a2b3] hover:text-[#344054] transition-colors"
          style={{ fontWeight: 500 }}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
        <div className="w-px h-4 bg-gray-100" />
        <span className="text-[14px] text-[#101828]" style={{ fontWeight: 700 }}>Usage</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-[#be43ff]" />
          </div>
        ) : (
          <div className="max-w-[700px] mx-auto px-6 py-8 space-y-8">

            {/* ── Plan overview ─────────────────────── */}
            <div className="rounded-2xl border border-gray-100 bg-white p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#f8f5ff] flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#be43ff]" />
                  </div>
                  <div>
                    <div className="text-[14px] text-[#101828]" style={{ fontWeight: 700 }}>
                      {planInfo?.plan === 'trial_expired' ? 'Trial (expired)' : (planInfo?.plan || 'Free')} Plan
                    </div>
                    <div className="text-[12px] text-[#667085]">
                      {planInfo?.creditsRemaining ?? 0} credits remaining
                      {planInfo && planInfo.creditsMonthly > 0 && ` of ${planInfo.creditsMonthly}`}
                    </div>
                  </div>
                </div>
                <span
                  className="text-[10px] px-2.5 py-1 rounded-full"
                  style={{
                    fontWeight: 600,
                    backgroundColor: planInfo?.trialExpired ? '#fef3cd' : '#ecfdf5',
                    color: planInfo?.trialExpired ? '#856404' : '#065f46',
                  }}
                >
                  {planInfo?.trialExpired ? 'Expired' : 'Active'}
                </span>
              </div>

              {/* Credits progress bar */}
              {planInfo && planInfo.creditsMonthly > 0 && (
                <div>
                  <div className="flex items-center justify-between text-[11px] text-[#667085] mb-1.5">
                    <span>Credits used</span>
                    <span style={{ fontWeight: 500 }}>{creditsUsedPct}%</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${creditsUsedPct}%`,
                        backgroundColor: creditsUsedPct > 80 ? '#ef4444' : creditsUsedPct > 50 ? '#f59e0b' : '#be43ff',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── Summary cards ─────────────────────── */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-gray-100 bg-white p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[#be43ff]" />
                  <span className="text-[11px] text-[#667085]" style={{ fontWeight: 500 }}>Est. Cost</span>
                </div>
                <div className="text-[20px] text-[#101828]" style={{ fontWeight: 700 }}>
                  {formatCost(totalCost)}
                </div>
                <div className="text-[10px] text-[#98a2b3]">Last {days} days</div>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-white p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-[#6366f1]" />
                  <span className="text-[11px] text-[#667085]" style={{ fontWeight: 500 }}>Tokens In</span>
                </div>
                <div className="text-[20px] text-[#101828]" style={{ fontWeight: 700 }}>
                  {formatTokens(totalTokensIn)}
                </div>
                <div className="text-[10px] text-[#98a2b3]">Input tokens</div>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-white p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-[#059669]" />
                  <span className="text-[11px] text-[#667085]" style={{ fontWeight: 500 }}>Tokens Out</span>
                </div>
                <div className="text-[20px] text-[#101828]" style={{ fontWeight: 700 }}>
                  {formatTokens(totalTokensOut)}
                </div>
                <div className="text-[10px] text-[#98a2b3]">Output tokens</div>
              </div>
            </div>

            {/* ── Period selector ──────────────────── */}
            <div className="flex items-center gap-1">
              {[7, 14, 30].map(d => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 rounded-lg text-[12px] transition-all ${
                    days === d
                      ? 'bg-[#be43ff] text-white'
                      : 'bg-gray-50 text-[#667085] hover:bg-gray-100'
                  }`}
                  style={{ fontWeight: 500 }}
                >
                  {d}d
                </button>
              ))}
            </div>

            {/* ── Agent breakdown ──────────────────── */}
            <div>
              <h3 className="text-[14px] text-[#101828] mb-3" style={{ fontWeight: 700 }}>
                Usage by Agent
              </h3>
              {!data?.agents.length ? (
                <p className="text-[12px] text-[#98a2b3] py-4">No usage data for this period.</p>
              ) : (
                <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  {data.agents.map((a, i) => {
                    const info = agentInfo(a.agentId);
                    const pct = totalCost > 0 ? Math.round((a.totalCostUsd / totalCost) * 100) : 0;
                    return (
                      <div
                        key={a.agentId}
                        className={`flex items-center gap-3 px-4 py-3.5 ${i < data.agents.length - 1 ? 'border-b border-gray-50' : ''}`}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-[14px] shrink-0"
                          style={{ backgroundColor: info.color + '18', color: info.color }}
                        >
                          {info.emoji}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
                              {info.name}
                            </span>
                            <span className="text-[10px] text-[#98a2b3] bg-gray-50 px-1.5 py-0.5 rounded" style={{ fontWeight: 500 }}>
                              {pct}%
                            </span>
                          </div>
                          <div className="text-[11px] text-[#98a2b3]">
                            {formatTokens(a.totalTokensIn)} in / {formatTokens(a.totalTokensOut)} out
                          </div>
                        </div>
                        {/* Mini bar */}
                        <div className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden shrink-0">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${pct}%`, backgroundColor: info.color }}
                          />
                        </div>
                        <div className="text-[13px] text-[#101828] w-16 text-right shrink-0" style={{ fontWeight: 600 }}>
                          {formatCost(a.totalCostUsd)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Daily trend ─────────────────────── */}
            {data && data.daily.length > 1 && (
              <div>
                <h3 className="text-[14px] text-[#101828] mb-3" style={{ fontWeight: 700 }}>
                  Daily Trend
                </h3>
                <div className="rounded-2xl border border-gray-100 bg-white p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-end gap-px h-24">
                    {(() => {
                      const maxCost = Math.max(...data.daily.map(d => d.costUsd), 0.001);
                      return data.daily.map((d, i) => (
                        <div
                          key={d.date}
                          className="flex-1 group relative"
                          title={`${d.date}: ${formatCost(d.costUsd)}`}
                        >
                          <div
                            className="w-full rounded-t bg-[#be43ff]/70 hover:bg-[#be43ff] transition-colors"
                            style={{
                              height: `${Math.max(2, (d.costUsd / maxCost) * 100)}%`,
                            }}
                          />
                          {/* Show label for first, last, and middle */}
                          {(i === 0 || i === data.daily.length - 1 || i === Math.floor(data.daily.length / 2)) && (
                            <div className="text-[9px] text-[#98a2b3] text-center mt-1 truncate">
                              {new Date(d.date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
